/**
 * Features related to interacting with a node which will be deprecated
 * and replaced with content
 * @module serives/node
 */
import { List, Set } from 'immutable'
import compact from 'lodash/compact'
import filter from 'lodash/filter'
import find from 'lodash/find'
import get from 'lodash/get'
import isFinite from 'lodash/isFinite'
import map from 'lodash/map'
import size from 'lodash/size'
import strictUriEncode from 'strict-uri-encode'
import { legacyBatchGet as getUserNodeInfo } from 'services/user-node-info'
import { map as mapPromise } from 'bluebird'
import { get as apiGet, TYPE_BROOKLYN_JSON, TYPE_BROOKLYN } from 'api-client'
import { createAuthForRequest, getAuthIsLoggedIn } from 'services/auth'
import './typedefs'

/**
 * Get a single node
 * @param {Object} [options={}] The options
 * @param {Number} options.id The content id (nid)
 * @param {String|String[]} options.language The content requested language
 * @param {import('immutable').Map|String} [options.auth] Optional authentication
 * @param {RequestSettings} [settings={}] The settings for request
 */
export async function fetchNode (options = {}, settings = {}) {
  const { exact = false } = settings
  const {
    id, xff, language, auth, contentType,
  } = options

  // This is not a valid id so don't make a request
  if (!isFinite(Number(id))) {
    return {}
  }

  const type = exact ? TYPE_BROOKLYN : TYPE_BROOKLYN_JSON
  try {
    const { body: data } = await apiGet(
      `node/${strictUriEncode(id)}`,
      { language, contentType, include4kImages: true },
      { xff, auth: createAuthForRequest({ auth, includeJwt: false }) },
      type,
    )

    return {
      id,
      data,
    }
  } catch (e) {
    return {
      id,
      error: e,
    }
  }
}

/**
 * Get multiple nodes by calling /node/:contentId for each id
 * @param {Object} [options={}] The options
 * @param {Number[]} options.ids A list of content ids
 * @param {String|String[]} options.language The content requested language
 * @param {import('immutable').Map|String} [options.auth] Optional authentication
 * @param {RequestSettings} [settings={}] The settings for request
 */
export async function fetchMultipleNodes (options = {}, settings = {}) {
  const { ids: idsArg, auth, ...restOptions } = options

  let ids = (List.isList(idsArg) || Set.isSet(idsArg)) ? idsArg.toArray() : idsArg
  // Remove invalid node ids
  ids = filter(ids, (id) => /^[0-9]+$/.test(id))

  // get notification
  if (!ids || size(ids) === 0) {
    return []
  }

  let nodes = await mapPromise(ids, async (id) => {
    try {
      return await fetchNode({ id, auth, ...restOptions }, settings)
    } catch (e) {
      // Do Nothing
    }
    return null
  })
  // Remove failed nodes which are list as null
  nodes = compact(nodes)

  // If auth is provided also get the user node info
  if (getAuthIsLoggedIn(auth)) {
    // TODO probably it's legacy and need to be replaced with node-user-info service
    const userInfoList = await getUserNodeInfo({ ids, auth })

    const dataWithUserInfo = map(nodes, (node) => {
      const userInfo = find(userInfoList, (n) => n.id === get(node, ['data', 'id']) || n.id === (node, ['data', 'nid']))
      return {
        ...node,
        data: {
          ...node.data,
          userInfo,
        },
      }
    })
    return dataWithUserInfo
  }
  return nodes
}

/**
 * @typedef TermRequestSettings
 * @property {String} [type=TYPE_BROOKLYN_JSON] TYPE_BROOKLYN | TYPE_BROOKLYN_JSON
 */

/**
 * getTerm Get single term
 * @param {Object} [options={}] The options
 * @param {Number[]} options.id term id
 * @param {String} options.language The content requested language
 * @param {import('immutable').Map|String} [options.auth] Optional authentication
 * @param {TermRequestSettings} [settings={}] The settings for request
 */
export async function fetchTerm (options = {}, settings = { type: TYPE_BROOKLYN_JSON }) {
  const { type } = settings
  const { id, language, auth } = options
  try {
    const { body: data } = await apiGet(
      `term/${id}`,
      { language },
      { auth: createAuthForRequest({ auth, includeJwt: false }) },
      type,
    )

    return {
      id,
      data,
    }
  } catch (e) {
    return {
      id,
      error: e,
    }
  }
}

/**
 * getTerms Get multiple terms
 * @param {Object} [options={}] The options
 * @param {Number[]} options.ids A list of terms ids
 * @param {String} options.language The content requested language
 * @param {import('immutable').Map|String} [options.auth] Optional authentication
 */
export async function fetchMultipleTerms (options = {}) {
  const { ids, language, auth } = options
  const terms = await Promise.all(ids.map(async (id) => {
    const term = await fetchTerm({ id, language, auth })
    return term
  }))
  return terms
}
